import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  BookableEmployeeType,
  CertificatesDataset,
  Prenotabili,
  VociPrenotabili,
} from "../../types";
import { useAuth } from "../../provider/authProvider";
import { getCertificates } from "./utils";
import { getBookableEmployeesBatch, getWorkplaces } from "../employee/utils";
import { getCourseCatalog } from "../course/utils";
import {
  conPrenotabile,
  mappaPrenotabili,
  senzaPrenotabile,
} from "../employee/prenotabili";

const DATASET_VUOTO: CertificatesDataset = {
  attestati: [],
  troncata: false,
  sediDiLavoro: [],
  catalogo: new Map(),
  loading: true,
};

type Contesto = {
  dataset: CertificatesDataset;
  richiedi: () => void;
  prenotabili: Prenotabili;
  richiediPrenotabili: () => void;
};

const CertificatesDatasetContext = createContext<Contesto | null>(null);

// Va montato una volta sulla rotta /attestati: il set viene recuperato al
// più una volta per visita alla rotta (e per token), alla prima tab che lo
// chiede, e condiviso da tutte le tab senza rifare le chiamate al cambio tab.
export function CertificatesDatasetProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { token } = useAuth();
  const [dataset, setDataset] = useState<CertificatesDataset>(DATASET_VUOTO);
  // Token per cui la richiesta è già partita: evita richieste doppie quando
  // più tab chiedono il set nello stesso momento.
  const tokenRichiesto = useRef<string | null>(null);

  const richiedi = useCallback(() => {
    if (!token || tokenRichiesto.current === token) return;
    tokenRichiesto.current = token;
    setDataset(DATASET_VUOTO);
    Promise.all([getCertificates(), getWorkplaces(), getCourseCatalog()]).then(
      ([lista, sediDiLavoro, catalogo]) => {
        // Una risposta arrivata dopo un cambio di token è di un'altra sessione
        if (tokenRichiesto.current !== token) return;
        // `troncata` viaggia col set come gli attestati: le tab mostrano e
        // contano quello che c'è nel set, e l'avviso della pagina dice che non
        // è tutto.
        setDataset({
          attestati: lista.attestati,
          troncata: lista.troncata,
          sediDiLavoro,
          catalogo,
          loading: false,
        });
      }
    );
  }, [token]);

  // I prenotabili viaggiano a parte dal set: si chiedono una sola volta per
  // visita alla rotta (e per token) e in background, così la lista non aspetta
  // le tendine PRENOTA. Dopo una prenotazione o un'assegnazione la lista
  // aggiornata arriva col ricaricamento della pagina, come per il set; nel
  // frattempo le tendine si correggono in sessione. Se il batch fallisce le
  // tendine restano vuote e la prossima tab (o accordion) che le chiede
  // ritenta.
  const [voci, setVoci] = useState<VociPrenotabili | null>(null);
  const tokenPrenotabili = useRef<string | null>(null);

  const richiediPrenotabili = useCallback(() => {
    if (!token || tokenPrenotabili.current === token) return;
    tokenPrenotabili.current = token;
    setVoci(null);
    getBookableEmployeesBatch().then((risposta) => {
      if (tokenPrenotabili.current !== token) return;
      if (risposta === null) {
        tokenPrenotabili.current = null;
        setVoci({});
        return;
      }
      setVoci(mappaPrenotabili(risposta));
    });
  }, [token]);

  const segnaAssegnato = useCallback((codiceCorso: string, cf: string) => {
    setVoci((prev) => senzaPrenotabile(prev ?? {}, codiceCorso, cf));
  }, []);
  const segnaAnnullato = useCallback(
    (codiceCorso: string, voce: BookableEmployeeType) => {
      setVoci((prev) => conPrenotabile(prev ?? {}, codiceCorso, voce));
    },
    []
  );
  const prenotabili = useMemo<Prenotabili>(
    () => ({ voci, segnaAssegnato, segnaAnnullato }),
    [voci, segnaAssegnato, segnaAnnullato]
  );

  // Al cambio di token il set non vale più: la prossima tab che lo chiede
  // fa ripartire il recupero.
  useEffect(() => {
    if (tokenRichiesto.current !== null && tokenRichiesto.current !== token) {
      tokenRichiesto.current = null;
      richiedi();
    }
    if (
      tokenPrenotabili.current !== null &&
      tokenPrenotabili.current !== token
    ) {
      tokenPrenotabili.current = null;
      richiediPrenotabili();
    }
  }, [token, richiedi, richiediPrenotabili]);

  return (
    <CertificatesDatasetContext.Provider
      value={{ dataset, richiedi, prenotabili, richiediPrenotabili }}
    >
      {children}
    </CertificatesDatasetContext.Provider>
  );
}

function useContesto(): Contesto {
  const contesto = useContext(CertificatesDatasetContext);
  if (!contesto) {
    throw new Error(
      "useCertificatesDataset va usato dentro CertificatesDatasetProvider"
    );
  }
  return contesto;
}

// Hook della pipeline condivisa: la tab che lo usa riceve il set e ne
// innesca il recupero se non è ancora partito.
export function useCertificatesDataset(): CertificatesDataset {
  const { dataset, richiedi } = useContesto();
  useEffect(() => {
    richiedi();
  }, [richiedi]);
  return dataset;
}

// L'unico hook dei prenotabili: la tab che lo usa riceve le tendine di tutti
// i corsi (null finché il batch non è arrivato) con le correzioni di sessione,
// e ne innesca il recupero se non è ancora partito. Nessuna chiamata per
// corso: le voci di un corso sono `voci[codiceCorso]`.
export function usePrenotabili(): Prenotabili {
  const { prenotabili, richiediPrenotabili } = useContesto();
  useEffect(() => {
    richiediPrenotabili();
  }, [richiediPrenotabili]);
  return prenotabili;
}
